import { toast } from "sonner";
import { Download } from "lucide-react";
import api, { zar, downloadPdf } from "@/lib/api";
import { PageHeader, useFetch, Spinner, StatCard } from "@/components/portal/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Receipt, Clock, AlertTriangle } from "lucide-react";

function fmt(t) { return t ? new Date(t).toLocaleDateString("en-ZA", { dateStyle: "medium" }) : "—"; }

export default function Invoices() {
  const { data, loading } = useFetch(() => api.get("/portal/invoices").then((r) => r.data));
  if (loading) return <Spinner />;
  const inv = data.invoices;
  const outstanding = inv.filter((i) => i.status !== "Paid").reduce((a, i) => a + i.total_zar, 0);
  const paid = inv.filter((i) => i.status === "Paid").reduce((a, i) => a + i.total_zar, 0);

  const pay = async (i) => {
    try {
      const { data: pf } = await api.post(`/payments/payfast/${i.id}`, { origin_url: window.location.origin });
      const form = document.createElement("form");
      form.method = "POST"; form.action = pf.action;
      Object.entries(pf.fields).forEach(([k, v]) => {
        const inp = document.createElement("input");
        inp.type = "hidden"; inp.name = k; inp.value = v; form.appendChild(inp);
      });
      document.body.appendChild(form); form.submit();
    } catch { toast.error("Could not start payment"); }
  };

  return (
    <div>
      <PageHeader title="Invoices" subtitle="Billing and payment history" />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard label="Total Invoices" value={inv.length} icon={Receipt} />
        <StatCard label="Paid" value={zar(paid)} icon={Clock} accent="text-emerald-600" />
        <StatCard label="Outstanding" value={zar(outstanding)} icon={AlertTriangle} accent="text-red-600" />
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr><th className="p-4">Invoice #</th><th className="p-4 hidden md:table-cell">Shipment</th><th className="p-4">Total</th><th className="p-4 hidden lg:table-cell">Due</th><th className="p-4">Status</th><th className="p-4"></th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {inv.map((i) => (
              <tr key={i.id} data-testid={`invoice-${i.invoice_number}`} className="hover:bg-muted/40">
                <td className="p-4 font-mono font-semibold">{i.invoice_number}</td>
                <td className="p-4 hidden md:table-cell font-mono text-muted-foreground">{i.tracking_number}</td>
                <td className="p-4 font-semibold">{zar(i.total_zar)}</td>
                <td className="p-4 hidden lg:table-cell text-muted-foreground">{fmt(i.due_date)}</td>
                <td className="p-4"><StatusBadge status={i.status} /></td>
                <td className="p-4">
                  <div className="flex items-center gap-1 justify-end">
                    {i.status !== "Paid" && <Button size="sm" className="bg-primary hover:bg-red-700 h-8" data-testid={`inv-pay-${i.invoice_number}`} onClick={() => pay(i)}>Pay</Button>}
                    {i.status === "Paid" && <Button variant="outline" size="sm" className="h-8" data-testid={`inv-receipt-${i.invoice_number}`} onClick={async () => { try { const r = await api.post(`/portal/invoices/${i.id}/receipt`); r.data.ok ? toast.success(`Receipt emailed to ${r.data.sent_to}`) : toast.message("Receipt endpoint ok (email not configured)"); } catch { toast.error("Could not send receipt"); } }}>Receipt</Button>}
                    <Button variant="ghost" size="sm" data-testid={`inv-dl-${i.invoice_number}`} onClick={async () => { try { await downloadPdf(`/portal/invoices/${i.id}/pdf`, `${i.invoice_number}.pdf`); toast.success("Invoice downloaded"); } catch { toast.error("Download failed"); } }}><Download size={16} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
